import React, { useState, useEffect, useCallback } from "react";
import { Card, Select, DatePicker, TimePicker, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Footer from "../components/Footer";
import Header from "../components/Header";
import moment from "moment-timezone";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";
import StandardButton from '../components/StandardButton';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

const TimezoneConverter = () => {
  const browserTimeZone = moment.tz.guess();
  const [datetime, setDatetime] = useState(dayjs());
  const [fromZone, setFromZone] = useState(browserTimeZone);
  const [toZone, setToZone] = useState("US/Pacific");
  const [converted, setConverted] = useState("");
  const [sourceTime, setSourceTime] = useState("");
  const [sourceZone, setSourceZone] = useState("");
  const [resultZone, setResultZone] = useState("");
  const { t } = useTranslation();
  const timezones = moment.tz.names();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/timezone-converter/share/${id}`)
        .then(res => {
          const data = res.data;
          if (data.datetime && data.fromZone && data.toZone && data.converted) {
            setDatetime(dayjs(data.datetime));
            setFromZone(data.fromZone);
            setToZone(data.toZone);
            setConverted(data.converted);
            setSourceZone(data.fromZone);
            setResultZone(data.toZone);
            setSourceTime(dayjs(data.datetime).format("YYYY-MM-DD HH:mm:ss"));
          }
        })
        .catch(() => {
          toast.error(t('server_error'));
        });
    }
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(converted);
      toast.success(t('copy_success'));
    } catch {
      toast.error(t('failed_to_copy'));
    }
  };

  const handleShare = async () => {
    try {
      const res = await api.post(`/timezone-converter/share`, {
        datetime: datetime.toISOString(),
        fromZone,
        toZone,
        converted
      });

      const data = res.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/timezone-converter?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      } else {
        toast.error(t('share_failed'));
      }
    } catch {
      toast.error(t('share_failed'));
    }
  };

  const handleConvert = useCallback(() => {
    if (!datetime) {
      message.error(t('please_select_date_time'));
      return;
    }

    try {
      const dt = dayjs.tz(datetime.format("YYYY-MM-DD HH:mm"), fromZone);
      const convertedTime = dt.tz(toZone).format("YYYY-MM-DD HH:mm:ss");
      setConverted(convertedTime);
      setSourceTime(dt.format("YYYY-MM-DD HH:mm:ss"));
      setSourceZone(fromZone);
      setResultZone(toZone);
    } catch (err) {
      message.error(t('failed_to_convert_time'));
    }
  }, [datetime, fromZone, toZone, t]);

  const handleNow = () => {
    setDatetime(dayjs());
  };

  const handleSwap = () => {
    setFromZone(toZone);
    setToZone(fromZone);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Enter" || !(e.metaKey || e.ctrlKey)) return;
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      handleConvert();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleConvert]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t('timezone_converter_title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('timezone_converter_intro')}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block font-medium mb-2">{t('select_date_time')}</label>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <div className="flex flex-col gap-4 md:flex-row md:flex-1">
                    <DatePicker
                      className="w-full"
                      value={datetime}
                      onChange={(date) =>
                        setDatetime(date ? date.hour(datetime.hour()).minute(datetime.minute()) : null)
                      }
                    />
                    <TimePicker
                      className="w-full"
                      format="HH:mm"
                      value={datetime}
                      onChange={(time) => {
                        if (datetime && time) {
                          setDatetime(datetime.hour(time.hour()).minute(time.minute()));
                        }
                      }}
                    />
                  </div>
                  <StandardButton
                    onClick={handleNow}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    aria-label={t('timezone_converter_now_aria')}
                  >
                    {t('timezone_converter_now_button')}
                  </StandardButton>
                </div>
              </div>

              <div>
                <label className="block font-medium mb-2">{t('from_timezone')}</label>
                <Select
                  showSearch
                  value={fromZone}
                  onChange={setFromZone}
                  className="w-full"
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {timezones.map((tz) => (
                    <Option key={tz} value={tz}>
                      {tz}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="flex justify-center -my-1">
                <StandardButton
                  onClick={handleSwap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  aria-label={t('timezone_converter_swap_aria')}
                >
                  {t('timezone_converter_swap_button')}
                </StandardButton>
              </div>

              <div className="mb-4">
                <label className="block font-medium mb-2">{t('to_timezone')}</label>
                <Select
                  showSearch
                  value={toZone}
                  onChange={setToZone}
                  className="w-full"
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                  {timezones.map((tz) => (
                    <Option key={tz} value={tz}>
                      {tz}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="mb-4 mt-4">
              <StandardButton
                onClick={handleConvert}
                type="secondary"
                className="w-full"
              >
                {t('convert_button')}
              </StandardButton>
            </div>

            {converted && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                {sourceTime && (
                  <div className="mb-3 pb-3 border-b border-indigo-200">
                    <p className="text-sm text-gray-600">
                      {t('timezone_converter_source_time_label')} <strong>{sourceZone}</strong>
                    </p>
                    <p className="mt-1 text-base text-gray-700 font-mono">{sourceTime}</p>
                  </div>
                )}
                <p className="text-sm text-gray-600">
                  {t('local_time_label')} <strong>{resultZone || toZone}</strong>
                </p>
                <p className="mt-1 text-lg text-gray-800 font-mono">{converted}</p>
                <div className="mt-4 flex gap-3">
                  <StandardButton
                    onClick={handleCopy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('copy_button')}
                  </StandardButton>
                  <StandardButton
                    onClick={handleShare}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t('share_button')}
                  </StandardButton>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default TimezoneConverter;
